import { getTrial } from "@/lib/trial";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";

const HERO =
  "https://images.unsplash.com/photo-1747331796135-0e2354a712e4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=900";

export default function AuthPage({ mode }) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signIn(mode, email.trim(), password, isLogin ? {} : { accept_terms: agree });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="relative max-w-md mx-auto min-h-screen bg-[#0A0A0A] flex flex-col">
        <div className="relative h-[38vh] min-h-[240px] overflow-hidden">
          <img src={HERO} alt="Two athletes grappling" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-[#E50914]/10 mix-blend-multiply" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="font-display text-5xl font-bold tracking-tight leading-none" data-testid="brand-logo">
              ROLL <span className="text-[#E50914]">SMARTR</span>
            </div>
            <p className="text-[#D4D4D8] mt-3 text-[15px] max-w-[300px]">
              Type how training went. Get back what worked, what failed, and what to drill.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="flex-1 px-6 pt-8 pb-10 flex flex-col gap-4 rise" data-testid="auth-form">
          <h1 className="font-display text-2xl font-semibold uppercase tracking-tight">
            {isLogin ? "Welcome back" : "Create account"}
          </h1>
          {!isLogin && getTrial() && <p className="text-sm text-[#A1A1AA] -mt-2" data-testid="auth-trial-note">Your session analysis will be saved to your journal.</p>}
          <input
            data-testid="auth-email-input"
            type="email"
            autoComplete="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field"
          />
          <input
            data-testid="auth-password-input"
            type="password"
            autoComplete={isLogin ? "current-password" : "new-password"}
            required
            minLength={6}
            placeholder={isLogin ? "Password" : "Password (6+ characters)"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field"
          />
          {!isLogin && (
            <label className="flex items-start gap-3 text-sm text-[#A1A1AA] cursor-pointer select-none">
              <input
                data-testid="auth-terms-checkbox"
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 w-5 h-5 shrink-0 accent-[#E50914]"
              />
              <span>
                I'm 13 or older and agree to the{" "}
                <Link to="/terms" data-testid="auth-terms-link" className="text-white underline decoration-[#E50914] underline-offset-4">Terms of Use</Link> and{" "}
                <Link to="/privacy" data-testid="auth-privacy-link" className="text-white underline decoration-[#E50914] underline-offset-4">Privacy Policy</Link>.
              </span>
            </label>
          )}
          {error && (
            <div data-testid="auth-error" className="text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl px-4 py-3">
              {error}
            </div>
          )}
          <button data-testid="auth-submit-button" disabled={busy || (!isLogin && !agree)} className="btn-primary mt-2">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <>{isLogin ? "Log in" : "Sign up"} <ArrowRight size={18} /></>}
          </button>
          <p className="text-sm text-[#A1A1AA] text-center mt-2">
            {isLogin ? "New to Roll SmartR? " : "Already have an account? "}
            <Link
              data-testid="auth-switch-link"
              to={isLogin ? "/register" : "/login"}
              className="text-white font-semibold underline decoration-[#E50914] underline-offset-4"
            >
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
          <p className="text-xs text-center text-[#52525B] mt-auto pt-6">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span className="mx-2">·</span>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
